import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider, useAuth } from 'react-oidc-context';
import i18n from 'i18next';
import { initReactI18next, I18nextProvider } from 'react-i18next';
import { DraggableContainer, en as helixTranslations, HELIX_REACT_AI_TRANSLATION_NAMESPACE } from '@ideagen/helix-react-ai';
import sessionManager from '../../store/modules/session/sessionManager';
import store from '../../store';
// Simplified AuthProvider that provides a basic auth context
const SimpleAuthProvider = ({ children }) => {
    return (_jsx(AuthProvider, { authority: "https://login.microsoftonline.com/common/v2.0", client_id: "mock-client-id", redirect_uri: window.location.origin, automaticSilentRenew: false, skipSigninCallback: true, loadUserInfo: false, children: _jsx(AuthUserProvider, { children: children }) }));
};
// Stable component that provides user data to the auth context
const AuthUserProvider = React.memo(({ children }) => {
    const auth = useAuth();
    // Use useRef to avoid re-renders and component reloads 
    const initializationRef = React.useRef(false);
    React.useEffect(() => {
        if (!initializationRef.current && auth && typeof auth.signinSilent === 'function') {
            initializationRef.current = true;
            // Create async function to handle token dispatch
            const initializeAuth = async () => {
                try {
                    // Use dispatch pattern like in App.vue (Option 2)
                    const token = await store.dispatch('cs-session/getToken') || '1234567890';
                    const userProfile = store.getters['session/user'];
                    // Create a simple mock user and inject it
                    const mockUser = {
                        id_token: token,
                        access_token: token,
                        profile: {
                            name: userProfile?.displayName || userProfile?.name?.givenName || 'User',
                            email: userProfile?.email || 'user@example.com',
                            sub: userProfile?.email || 'user-123'
                        },
                        expired: false,
                        expires_at: Math.floor(Date.now() / 1000) + 3600
                    };
                    // Set the user directly in localStorage for oidc-client to pick up
                    const userKey = `oidc.user:${window.location.origin}:mock-client-id`;
                    localStorage.setItem(userKey, JSON.stringify(mockUser));
                    // Force the auth context to reload the user without timeout
                    if (auth._userManager?.getUser) {
                        auth._userManager.getUser();
                    }
                }
                catch (error) {
                    console.warn('Could not initialize auth or set user in localStorage:', error);
                }
            };
            // Execute the async initialization
            initializeAuth();
        }
    }, [auth]); // Remove initialized from deps to prevent re-renders
    return React.createElement(React.Fragment, null, children);
});
// Stable error boundary wrapper for React components in Vue
const ReactErrorBoundary = React.memo(({ children }) => {
    // Use ref to avoid state-based re-renders that cause component reloads
    const errorRef = React.useRef(false);
    const [, forceUpdate] = React.useReducer(x => x + 1, 0);
    React.useEffect(() => {
        const handleError = (error) => {
            console.error('React Error in Vue context:', error);
            if (!errorRef.current) {
                errorRef.current = true;
                forceUpdate(); // Force update once without causing full reload
            }
        };
        window.addEventListener('error', handleError);
        return () => window.removeEventListener('error', handleError);
    }, []); // Empty deps to prevent re-renders
    if (errorRef.current) {
        return React.createElement('div', {
            style: {
                padding: '20px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '4px',
                textAlign: 'center'
            }
        }, 'AI Assistant temporarily unavailable');
    }
    return React.createElement(React.Fragment, null, children);
});
const DraggableContainerWrapper = React.memo((props) => {
    return React.createElement(DraggableContainer, props);
}, (prevProps, nextProps) => {
    // Optimized comparison - only re-render when props actually change
    return (prevProps.open === nextProps.open &&
        prevProps.dataId === nextProps.dataId &&
        prevProps.token === nextProps.token &&
        prevProps.onClose === nextProps.onClose &&
        prevProps.isEmbedded === nextProps.isEmbedded &&
        JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config));
});
// Initialize i18n with helix translations
i18n.use(initReactI18next).init({
    lng: 'en',
    fallbackLng: 'en',
    resources: {
        en: {
            translation: {
                // Basic translations - add more as needed
                'loading': 'Loading...',
                'close': 'Close',
                'error': 'Error'
            },
            [HELIX_REACT_AI_TRANSLATION_NAMESPACE]: helixTranslations
        }
    },
    interpolation: {
        escapeValue: false
    }
});
/**
 * Inner component that uses auth context - memoized to prevent unnecessary re-renders
 */
const AuthenticatedApp = React.memo(({ open = false, onClose, vueToken }) => {
    const auth = useAuth();
    // Memoize the close handler to prevent re-renders
    const handleClose = React.useCallback(() => {
        if (onClose) {
            onClose();
        }
    }, [onClose]);
    // Get token with Vue token as default/fallback - start with vueToken for initial render
    const [storeToken, setStoreToken] = React.useState(vueToken || null);
    React.useEffect(() => {
        const fetchTokenFromStore = async () => {
            try {
                // Use Vue store dispatch to get token
                const token = await store.dispatch('cs-session/getToken');
                setStoreToken(token || vueToken); // Use Vue token as fallback
            }
            catch (error) {
                console.warn('Failed to dispatch cs-session/getToken in Connector.tsx:', error);
                // Fallback to Vue token if dispatch fails
                if (vueToken) {
                    setStoreToken(vueToken);
                }
            }
        };
        fetchTokenFromStore();
    }, [vueToken]); // Re-run when Vue token changes
    // Memoize the config to prevent re-renders
    const mazlanConfig = React.useMemo(() => ({
        agent_url: process.env.MAZLAN_AGENT_URL,
        welcome_message: "Hi I'm Mazlan, \n\nI’m here to make working with your policies easier — whether you want to create, improve, review, or summarise.\n\nPlease choose what you’d like to do first:",
        prompt_suggestions: [`I want to create a policy`, `I want to improve a policy I have created`, `I want to review a policy I have created against CompliSpace's policy framework`, `I want to summarise a policy`, `I want to ask questions about a specific policy`, `I want to add context keys to a policy`],
        custom_settings: {
            "ce-app-api-url": process.env.CONTENT_ENGINE_URL || '',
            "x-site-uri": sessionManager.getXSiteUrl() || '',
            "ce-app-api-authorization": `Bearer ${storeToken}`
        }
    }), [storeToken, vueToken]); // Update when tokens change
    // Use token with multiple fallbacks: store dispatch -> Vue prop -> auth context -> default
    const finalToken = React.useMemo(() => {
        const token = storeToken || vueToken || auth?.user?.id_token;
        return token;
    }, [storeToken, vueToken, auth?.user?.id_token]);
    return (_jsx(ReactErrorBoundary, { children: _jsx(I18nextProvider, { i18n: i18n, children: _jsx(DraggableContainerWrapper, { dataId: "draggable-container-sample", open: open, onClose: handleClose, token: finalToken, config: mazlanConfig, isEmbedded: false }) }) }));
});
/**
 * Main App component with DraggableContainer - memoized to prevent re-renders
 */
const App = React.memo(({ open = false, onClose, vueToken }) => {
    // Use simple auth provider that injects user data from Vue store
    return (_jsx(SimpleAuthProvider, { children: _jsx(AuthenticatedApp, { open: open, onClose: onClose, vueToken: vueToken }) }));
});
/**
 * Autonomous React App that manages its own lifecycle and state
 */
class AutonomousReactApp {
    constructor(targetEl) {
        this.isOpen = false;
        this.onCloseCallback = null;
        this.vueToken = null;
        // Simple React root without complex configuration
        this.root = createRoot(targetEl, {
            identifierPrefix: 'mazlan-',
            // Minimal production configuration
            ...(process.env.NODE_ENV === 'production' && {
                onRecoverableError: (error, errorInfo) => {
                    console.warn('React error:', error);
                }
            })
        });
        // React mounts itself immediately and manages its own state
        this.renderApp();
    }
    // Vue calls this to update React's internal state
    updateState(isOpen, onClose, token) {
        this.isOpen = isOpen;
        this.onCloseCallback = onClose || null;
        this.vueToken = token || null;
        this.renderApp(); // Re-render with new state
    }
    renderApp() {
        try {
            // React handles everything internally - Vue just provides state updates
            this.root.render(_jsx(App, { open: this.isOpen, vueToken: this.vueToken || undefined, onClose: () => {
                    // React handles the close internally
                    this.isOpen = false;
                    this.renderApp(); // Close the dialog in React
                    // Then notify Vue
                    if (this.onCloseCallback) {
                        this.onCloseCallback();
                    }
                } }));
        }
        catch (error) {
            console.error('Failed to render React component:', error);
            this.root.render(React.createElement('div', { style: { padding: '20px', textAlign: 'center' } }, 'AI Assistant temporarily unavailable'));
        }
    }
    unmount() {
        try {
            this.root.unmount();
        }
        catch (error) {
            console.warn('Error during React unmount:', error);
        }
    }
}
/**
 * Simple connector interface for Vue - React manages itself
 */
export class ReactConnector {
    constructor(targetEl) {
        // React app starts immediately and manages its own lifecycle
        this.app = new AutonomousReactApp(targetEl);
    }
    // Simplified API - Vue just tells React what state it should be in
    render(props = {}) {
        this.app.updateState(props.open || false, props.onClose, props.token);
    }
    unmount() {
        this.app.unmount();
    }
}
export default App;
